<template>
<!-- eslint-disable max-len -->
<div class="radial-progress">
  <div
    class="circular-fill"
    :style="{
      '--fill': colorRGB,
      '--value': `${getPercentFromValue(displayValue)}%`,
    }"
  >
  </div>
  <p class="value-flex-outer">
    <span
      class="value-flex-inner"
      :style="game.metricResultsPercentageStyle ? game.metricResultsPercentageStyle : undefined"
    >
      <div class="value">{{ displayValue.toFixed(decimalPlaces) }}</div>
      <div class="percent-sign" v-if="percent && decimalPlaces === 0">%</div>
    </span>
    <small
      class="name"
      :style="game.metricResultsLabelStyle ? game.metricResultsLabelStyle : undefined"
    >{{ name }}</small>
  </p>
  <p class="min-label" v-if="true || min">{{ checkedMin }}</p>
  <p class="max-label" v-if="true || max && max !== 100">{{ checkedMax }}</p>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  props: {
    value: { type: Number },
    enableAnimationTickSound: { type: Boolean },
    percent: { type: Boolean },
    integer: { type: Boolean },
    min: { type: Number },
    max: { type: Number },
    color: { type: String },
    name: { type: String }
  },
  data() {
    return {
      displayValue: 0,
    };
  },
  computed: {
    ...mapGetters([
      'game'
    ]),
    clampedValue() {
      let { value } = this;
      if (this.percent) value = Math.max(0, Math.min(100, value));
      if (!isNaN(parseFloat(this.min))) {
        value = Math.max(this.min, value);
      }
      if (!isNaN(parseFloat(this.max))) {
        value = Math.min(this.max, value);
      }
      return value;
    },
    checkedMin() {
      return !isNaN(parseFloat(this.min)) && this.min || 0;
    },
    checkedMax() {
      return !isNaN(parseFloat(this.max)) && this.max || 100;
    },
    checkedRange() {
      return Math.abs(this.checkedMax - this.checkedMin) || 100;
    },
    displayAsInteger() {
      return this.percent || this.integer || this.displayValue >= 100 || this.displayValue <= -100;
    },
    decimalPlaces() {
      return (this.displayAsInteger ? 0 : 1);
    },
    multiplier() {
      return 10 ** this.decimalPlaces;
    },
    colorRGB() {
      return this.color.replace(/rgba?(\(\s*\d+\s*,\s*\d+\s*,\s*\d+)(?:\s*,.+?)?\)/, 'rgba$1,1)');
    },
  },
  created() {
    this.displayValue = this.clampedValue;
  },
  methods: {
    ...mapActions([
      'playSound',
    ]),
    getDecimalFromValue(value) {
      // radial display only makes sense for values in a range
      // so assume percent or capped vals
      return Math.abs(value - this.checkedMin) / this.checkedRange;
    },
    getPercentFromValue(value) {
      const fraction = this.getDecimalFromValue(value);
      return fraction * 100;
    },
    getDegreesFromValue(value) {
      const fraction = this.getDecimalFromValue(value);
      return fraction * 360;
    },
    getTransform(degrees) {
      return `rotate(${degrees}deg`;
    },
    lerp(v1, v2, t) {
      return v1 + (v2 - v1) * t;
    },
    round(value) {
      return Math.round(value * this.multiplier) / this.multiplier;
    },
    ceil(value) {
      return Math.ceil(value * this.multiplier) / this.multiplier;
    },
    floor(value) {
      return Math.floor(value * this.multiplier) / this.multiplier;
    }
  },
  watch: {
    clampedValue(newValue, oldValue) {
      const target = this.round(newValue);
      let initial = oldValue;

      // animate from zero
      if (target !== initial) {
        initial = this.checkedMin;
        this.displayValue = this.checkedMin;
      }

      const steps = 200;
      const duration = 3000;
      let step = 0;
      const soundFrequency = (target - initial !== 0) ? Math.round(steps / (target - initial)) : 2;
      const fillInterval = setInterval(() => {
        if (step > steps) {
          this.displayValue = target;
          return clearInterval(fillInterval);
        }
        // eslint-disable-next-line no-plusplus
        this.displayValue = this.lerp(initial, target, ++step / steps);
        if (
          this.enableAnimationTickSound
          && this.game && this.game.resultsAnimationTickSound
          && (step % soundFrequency === 0)
        ) {
          this.playSound({
            sound: this.game.resultsAnimationTickSound,
          });
        }
      }, duration / steps);
    }
  }
};
</script>

<style lang="scss" scoped>
.radial-progress {
  position: relative;
  background-color: white;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  display: inline-block;
  margin: 0 25px;
}
.circular-fill {
  --center: white;
  --fill: lightseagreen;
  --value: 0%;
  width: 200px;
  height: 200px;
  border: 32px solid transparent;
  border-radius: 50%;
  background:
    linear-gradient(var(--center),var(--center)) padding-box,
    conic-gradient(var(--fill) var(--value), #e5e5e5 var(--value)) border-box;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

// @keyframes progress-pulse {
//   0% {
//     background: hsla(255, 0%, 100%, 0);
//   }
//   50% {
//     background: hsla(255, 0%, 100%, 10%);
//   }
//   100% {
//     background: hsla(255, 0%, 100%, 0);;
//   }
// }

.value-flex-outer {
  color: black;
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0px;
  top: -15px;
  padding: 0 39px;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  transform: translateZ(1px); // fix for full-screen
}
.value-flex-inner {
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: baseline;

  // for the value
  color: black;
  font-size: 60px;
  font-weight: bold;
}
.percent-sign {
  font-size: 75%; // 45px (relative to support inherited size change)
}
.name {
  color: black;
  width: 100%;
  position: absolute;
  left: 0px;
  top: 133px;
  text-align: center;
  line-height: 16.75px;
  font-size: 15.5px;
  padding: 0 42px;
  font-weight: bold;
  overflow: hidden;
}
.min-label {
  position: absolute;
  left: 123px;
  top: 21px;
  color: white;
  visibility: hidden;
  transform: translateZ(1px); // fix for full-screen
}
.max-label {
  position: absolute;
  right: 123px;
  top: 21px;
  color: white;
  visibility: hidden;
  transform: translateZ(1px); // fix for full-screen
}
.metric-name {
  position: absolute;
  font-size: 50px;
  top: 50px;
  left: 0;
  display: block;
  height: 100%;
  width: 280px;
}
.metric-name-label {
  width: 100%;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/RadialProgress.vue